import React, { ReactNode } from 'react'

import { useNavigate } from 'react-router-dom'

import useInitializeAuth from '@/core/hooks/useInitializeAuth'
import { Spinner } from '@/shared/components/atoms/Spinner'
import MainLayout from '@/shared/components/organisms/Layout/MainLayout/MainLayout'

interface AuthenticatedLayoutProps {
	children: ReactNode
	showTopAppBar?: boolean
	topAppBarContent?: ReactNode
	topAppBarLeftContent?: ReactNode
	requireAuth?: boolean
	redirectTo?: string
	hideSidebar?: boolean
}

/**
 * A wrapper component that ensures the user is authenticated and loaded
 * before rendering the children. Handles loading states and redirects.
 */
const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
	children,
	showTopAppBar = true,
	topAppBarContent,
	topAppBarLeftContent,
	requireAuth = true,
	redirectTo = '/login',
	hideSidebar = false,
}) => {
	const navigate = useNavigate()
	const { isInitialized, user, isAuthenticated } = useInitializeAuth()

	// While the auth state is initializing, show a loading spinner
	if (!isInitialized) {
		return (
			<div className="flex justify-center items-center h-screen w-screen bg-baseBackground">
				<Spinner size="xl" />
			</div>
		)
	}

	// If auth is required but user is not authenticated, redirect
	if (requireAuth && !isAuthenticated) {
		// Redirect to login page
		navigate(redirectTo)
		return null
	}

	// Once initialized, render the main layout with user data
	return (
		<>
			<MainLayout
				isAuthenticated={isAuthenticated}
				user={user || undefined}
				showTopAppBar={showTopAppBar}
				topAppBarContent={topAppBarContent}
				topAppBarLeftContent={topAppBarLeftContent}
				className="bg-baseNeutral"
				hideSidebar={hideSidebar}
			>
				{children}
			</MainLayout>
		</>
	)
}

export default AuthenticatedLayout
